import { useCallback, useEffect, useRef } from 'react'
import { useDispatch, useStore } from 'react-redux'
import {
  dispatchAsync,
  fetchBulkOrderCatalog,
  normalizeCatalogId,
} from '../../common/bulkOrder'
import { apiGet } from '../../common/lib/api'
import fetch from '../../common/lib/fetch'

const CATALOG_META_POLL_MS = 60_000

const useBulkOrderCatalogSync = () => {
  const dispatch = useDispatch()
  const store = useStore()
  const inFlightRef = useRef(false)

  const syncCatalog = useCallback(async () => {
    if (inFlightRef.current) return

    const state = store.getState().bulkOrder
    if (state.isCatalogLoading) return

    const hasWarmCatalog = state.isCatalogReady

    inFlightRef.current = true
    try {
      if (!hasWarmCatalog) {
        await dispatchAsync(dispatch, fetchBulkOrderCatalog())
        return
      }

      try {
        const response = await apiGet({
          fetch,
          endpoint: 'bulk-order/catalog/meta',
          getState: store.getState,
        })
        const remoteCatalogId = normalizeCatalogId(response?.data?.catalogId)
        const currentCatalogId = normalizeCatalogId(
          store.getState().bulkOrder.catalogId,
        )

        if (remoteCatalogId !== currentCatalogId) {
          await dispatchAsync(dispatch, fetchBulkOrderCatalog())
        }
      } catch (error) {
        // Meta failures must not wipe a warm catalog.
      }
    } catch (error) {
      // Full-fetch errors are handled in the reducer.
    } finally {
      inFlightRef.current = false
    }
  }, [dispatch, store])

  useEffect(() => {
    syncCatalog()

    const intervalId = setInterval(() => {
      syncCatalog()
    }, CATALOG_META_POLL_MS)

    const handleVisibilityOrFocus = () => {
      if (
        typeof document !== 'undefined' &&
        document.visibilityState === 'hidden'
      ) {
        return
      }
      syncCatalog()
    }

    window.addEventListener('focus', handleVisibilityOrFocus)
    document.addEventListener('visibilitychange', handleVisibilityOrFocus)

    return () => {
      clearInterval(intervalId)
      window.removeEventListener('focus', handleVisibilityOrFocus)
      document.removeEventListener('visibilitychange', handleVisibilityOrFocus)
    }
  }, [syncCatalog])
}

export default useBulkOrderCatalogSync
